import { findReplacements, getCharName } from "@/utils/textSanitization";
import { captureGlyphSnippets } from "@/utils/pdfGlyphSnippet";

/**
 * Initializes the manual selection tool in WPD.
 */
function init(wpd, wpdDocument, dataEntryVM) {
	// cannot pick and choose functions to override due to how it was written
	// completely overwrite the manual selection tool into a js class
	wpd.TextSelectionTool = class {
		// how long selection must be unchanged before commit
		static SELECTION_STABLE_THRESHOLD_MS = 120;

		// typical os double-click interval
		static DBLCLICK_THRESHOLD_MS = 250;

		// within this many css px counts as same spot
		static DBLCLICK_THRESHOLD_PX = 5;

		#commitTimer = null;
		#isSelecting = false;
		#lastSelectionChangeAt = 0;
		#lastDownAt = 0;
		#lastDownPos = { x: 0, y: 0 };

		constructor(resolve, keydownHandler) {
			this.resolve = resolve;
			this.keydownHandler = keydownHandler;
			this.textLayer = wpdDocument.getElementById("pdf-text-layer");
			this.selectedText = "";
		}

		#enableTextLayer() {
			this.textLayer.style["z-index"] = 10;
		}

		#disableTextLayer() {
			this.textLayer.style["z-index"] = "auto";
		}

		#addEventListeners() {
			this.#removeEventListeners();
			this.textLayer.addEventListener("mousedown", this.#onMouseDown, true);
			this.textLayer.addEventListener("mouseup", this.#onMouseUp, true);

			wpdDocument.addEventListener(
				"selectionchange",
				this.#onSelectionChange,
				true,
			);
			wpdDocument.addEventListener("keydown", this.#onKeyDown, true);
		}

		#removeEventListeners() {
			this.textLayer.removeEventListener("mousedown", this.#onMouseDown, true);
			this.textLayer.removeEventListener("mouseup", this.#onMouseUp, true);

			wpdDocument.removeEventListener(
				"selectionchange",
				this.#onSelectionChange,
				true,
			);
			wpdDocument.removeEventListener("keydown", this.#onKeyDown, true);
		}

		#getSelectionTopLeft(selection) {
			if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
				return null;
			}

			const range = selection.getRangeAt(0);

			// make sure selection is inside the text layer
			const common = range.commonAncestorContainer;
			if (!this.textLayer.contains(common)) {
				return null;
			}

			// per-line/per-fragment boxes in DOM order (start to end)
			const rectList = range.getClientRects();
			if (!rectList || rectList.length === 0) {
				return null;
			}

			// position of the target element (the canvas)
			const target = wpd.graphicsWidget.$mainCanvas;
			const canvasRect = target.getBoundingClientRect();

			// top-left in css px relative to the target element
			const xCss = rectList[0].left - canvasRect.left;
			const yCss = rectList[0].top - canvasRect.top;

			// convert css to canvas px
			let scaleX = 1;
			let scaleY = 1;
			if (target instanceof HTMLCanvasElement) {
				scaleX = target.width / canvasRect.width;
				scaleY = target.height / canvasRect.height;
			}

			return wpd.graphicsWidget.imagePx(xCss * scaleX, yCss * scaleY);
		}

		// Zoomed crops of the dropped glyph(s) (one per unrecognized character)
		// for the sanitization dialog's per-row source snippets. The WPD canvas
		// lives in an iframe realm, so we hand the shared helper the iframe's
		// canvas + document.
		#captureSelectionSnippets(selection) {
			if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
				return [];
			}
			const range = selection.getRangeAt(0);
			if (!this.textLayer.contains(range.commonAncestorContainer)) return [];
			return captureGlyphSnippets(
				range,
				wpd.graphicsWidget.$mainCanvas,
				wpdDocument,
			);
		}

		#scheduleCommit(afterMs) {
			if (this.#commitTimer) {
				clearTimeout(this.#commitTimer);
				this.#commitTimer = null;
			}
			this.#commitTimer = setTimeout(() => this.#maybeCommit(), afterMs);
		}

		#cancelPendingCommit() {
			if (this.#commitTimer) {
				clearTimeout(this.#commitTimer);
				this.#commitTimer = null;
			}
		}

		#maybeCommit() {
			const selection = wpdDocument.getSelection();
			if (!selection || selection.isCollapsed) return;

			const range = selection.getRangeAt(0);
			if (!this.textLayer.contains(range.commonAncestorContainer)) return;

			// ensure selection has settled
			const now = performance.now();
			if (
				now - this.#lastSelectionChangeAt <
				wpd.TextSelectionTool.SELECTION_STABLE_THRESHOLD_MS
			) {
				this.#scheduleCommit(
					wpd.TextSelectionTool.SELECTION_STABLE_THRESHOLD_MS,
				);
				return;
			}

			const rawText = selection.toString().trim();

			let replacementDetails = [];
			if (dataEntryVM.textSelectCharacterSanitization) {
				replacementDetails = findReplacements(rawText);
			}

			this.wasSanitized = replacementDetails.length > 0;
			this.originalText = this.wasSanitized ? rawText : null;
			this.replacementDetails = replacementDetails;

			if (!this.wasSanitized) {
				this.selectedText = rawText.replace(/\s+/g, " ").replace(/-\s/g, "-");
			} else {
				// Keep raw text for sanitization dialog
				this.selectedText = rawText;
			}

			if (this.selectedText.length > 0) {
				this.#textSelected();
				selection.empty();
			}

			this.#isSelecting = false;
		}

		#textSelected() {
			const includeLocation =
				wpd.tagGroups.directTextSelect && !wpd.tagGroups.isEditing;

			this.resolve({
				text: this.selectedText,
				originalText: this.wasSanitized ? this.originalText : null,
				wasSanitized: this.wasSanitized,
				replacementDetails: this.wasSanitized ? this.replacementDetails : null,
				location: includeLocation
					? this.#getSelectionTopLeft(wpdDocument.getSelection())
					: null,
				// Per-character crops from the page canvas, so the sanitization
				// dialog can show what each dropped glyph looks like. Only needed
				// when the dialog will open (wasSanitized).
				sourceImages: this.wasSanitized
					? this.#captureSelectionSnippets(wpdDocument.getSelection())
					: [],
			});

			this.detach();
		}

		// fat-arrow function allows "this" to be the instance of this class
		// instead of having it deteremined by the caller
		#onKeyDown = (event) => {
			this?.keydownHandler(event);
		};

		#onMouseDown = (event) => {
			const now = performance.now();
			const x = event.clientX;
			const y = event.clientY;

			// check if this is a double-click
			// if event.detail >= 2 or mousedown within time and distance of the previous click
			// then assume it is a double-click + drag selection
			const withinTime =
				now - this.#lastDownAt <= wpd.TextSelectionTool.DBLCLICK_THRESHOLD_MS;
			const withinDist =
				Math.abs(x - this.#lastDownPos.x) <=
					wpd.TextSelectionTool.DBLCLICK_THRESHOLD_PX &&
				Math.abs(y - this.#lastDownPos.y) <=
					wpd.TextSelectionTool.DBLCLICK_THRESHOLD_PX;

			if (event.detail >= 2 || (withinTime && withinDist)) {
				// start of the second click, enter word-selecting mode
				this.#isSelecting = true;

				// cancel any single-click pending commit
				this.#cancelPendingCommit();
			}

			// record this mousedown for next check
			this.#lastDownAt = now;
			this.#lastDownPos = { x, y };
		};

		#onMouseUp = () => {
			const selection = wpdDocument.getSelection();
			if (!selection || selection.isCollapsed) return;

			if (this.#isSelecting) {
				// handles double-click and double-click + drag
				// wait briefly for selection to settle before committing
				this.#scheduleCommit(
					wpd.TextSelectionTool.SELECTION_STABLE_THRESHOLD_MS,
				);
				return;
			}

			// single click + drag case
			// wait to commit in case a second click is coming
			// start a commit timer equal to the double-click threshold
			// if a second mousedown happens in that window, #onMouseDown will
			// cancel this by calling #cancelPendingCommit
			this.#scheduleCommit(wpd.TextSelectionTool.DBLCLICK_THRESHOLD_MS);
		};

		#onSelectionChange = () => {
			// record the time of the last selection change
			this.#lastSelectionChangeAt = performance.now();
		};

		attach() {
			this.#addEventListeners();

			this.#enableTextLayer();
		}

		detach() {
			this.#disableTextLayer();

			this.#removeEventListeners();
		}
	};
}

export { init };
